<template>
  <div>
    <label v-if="label" :for="id" :class="customClass.labelClass">{{
      label
    }}</label>
    <div
      ref="inputWrapperRef"
      @click="handleClickWrapper"
      :class="['input-wrapper', customClass.containerClass]"
    >
      <div class="arrow-icon">
        <slot name="dropdownIndicator">
          <img
            src="~@/assets/icon/chevron-up-down.svg"
            alt="arrow"
            width="12"
            height="12"
          />
        </slot>
      </div>
      <div class="value-wrapper">
        <div
          :class="[
            !showInput && (!value || value.length <= 0) ? 'block' : 'hidden',
          ]"
        >
          <p class="m-0 input-placeholder mt-1">{{ placeholder }}</p>
        </div>
        <div
          v-for="(value, index) in value"
          :key="index"
          :class="['value-list', customClass.valueListClass]"
        >
          <p class="m-0">{{ value.label }}</p>
          <div @click.stop="removeValue(value)">
            <i class="fa fa-times"></i>
          </div>
        </div>
        <div
          :class="[
            'value-input',
            { 'height-0 flex-1': !showInput },
            { 'w-100': showInput },
            customClass.inputClass,
          ]"
        >
          <input @input="handleChangeInput" ref="valueInputRef" type="text" />
        </div>
      </div>
      <div
        v-if="showOptions"
        :class="['option-container', customClass.optionContainerClass]"
      >
        <div
          @click="handleClickOption(option)"
          v-for="(option, index) in currentOptions"
          :key="index"
          :class="[
            'option-list',
            {
              'option-list-selected': value.find(
                (el) => el.value === option.value
              ),
            },
            customClass.optionListClass,
          ]"
        >
          {{ option.label }}
        </div>
      </div>
    </div>
    <small v-if="currentError" class="text-danger">{{ currentError }}</small>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";

export default {
  name: "MultiSelect",
  components: {
    Multiselect,
  },
  props: {
    label: { type: String, default: "" },
    options: { type: Array, required: true },
    placeholder: { type: String, default: "", required: false },
    error: { type: String, default: "", required: false },
    value: { type: Array, default: () => [] },
    required: { type: Boolean, default: false },
    id: { type: String, required: true },
    customClass: {
      type: Object,
      default: () => ({
        containerClass: "",
        labelClass: "",
        inputClass: "",
        valueListClass: "",
        optionListClass: "",
        optionContainerClass: "",
      }),
    },
  },
  data() {
    return {
      showInput: false,
      showOptions: false,
      currentOptions: [],
      currentError: this.error,
      currentValue: this.value,
    };
  },
  methods: {
    handleClickWrapper() {
      this.showInput = true;
      this.showOptions = true;
      this.currentOptions = this.options;
    },
    handleClickOutside(event) {
      if (!this.$refs.inputWrapperRef.contains(event.target)) {
        if (
          this.required &&
          this.showOptions &&
          (!this.currentValue || this.currentValue.length <= 0)
        ) {
          this.currentError = "This field is required";
        }
        this.showInput = false;
        this.showOptions = false;
      }
    },
    handleChangeInput(event) {
      this.currentOptions = this.options.filter((option) =>
        option.label.toLowerCase().includes(event.target.value.toLowerCase())
      );
    },
    handleClickOption(option) {
      if (this.value.find((el) => el.value === option.value)) return;
      const result = [...this.value, option];
      this.currentValue = result;
      if (result.length > 0) this.currentError = "";
      this.$emit("change", result);
      this.showInput = false;
      this.showOptions = false;
    },
    removeValue(value) {
      const result = this.value.filter((item) => item.value !== value.value);
      this.currentValue = result;
      if (result.length <= 0) this.currentError = "This field is required";
      this.$emit("change", result);
    },
  },
  watch: {
    showInput(val) {
      if (val) {
        this.$refs.valueInputRef.value = "";
        this.$refs.valueInputRef.focus();
      }
    },
    error(value) {
      this.currentError = value;
    },
    value(value) {
      this.currentValue = value;
    },
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
  },
};
</script>

<style scoped>
.hidden {
  display: none;
}

.block {
  display: block;
}

label {
  color: #374151;
  font-weight: 600;
  font-size: 14px;
}

.height-0 {
  height: 0;
}

.input-wrapper {
  position: relative;
  border: 1px solid #ced4da;
  min-height: 38px;
  border-radius: 6px;
  padding: 5px 10px;
  padding-right: 30px;
  cursor: pointer;
}

.option-container {
  border: 1px solid #ced4da;
  border-radius: 6px;
  padding: 5px 0px;
  min-height: 36px;
  min-width: 100%;
  position: absolute;
  margin-bottom: 10px;
  background: #ffffff;
  top: 102%;
  left: 0;
  z-index: 99;
  max-height: 300px;
  overflow-y: auto;
}

.option-list {
  padding: 5px 10px;
}

.option-list:hover,
.option-list-selected {
  background: #ecf0f1;
}

.value-wrapper {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}

.value-list {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 5px;
  background: #f5f6fa;
  border: 1px solid #ced4da;
  border-radius: 6px;
  padding: 2px 6px;
  font-size: 1rem;
}

.value-list .fa-times {
  cursor: pointer;
}

.flex-1 {
  flex: 1;
}

.value-input {
  overflow: hidden;
}

.value-input input {
  border: none;
  outline: none;
  width: 100%;
  height: 100%;
  font-size: 1rem;
}

.arrow-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}

.input-placeholder {
  font-size: 1rem;
  color: #2e364666;
}
</style>


// WEBPACK FOOTER //
// src/components/Input/MultiSelect.vue